import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { routes } from "../../appRoutingHelpers";
import { OrganizationKeyName } from "../../model/Organization";
import { PromotionCodeListItem } from "../../service/api-response/PromotionCodesSuccessResponse";
import * as promotionCodeSelectors from "./promotionCodeSelectors";
import * as promotionCodeActions from "./promotionCodeActions";
import { RootState } from "../../store";
import Spinner from "../common/Spinner";
import * as authSelectors from "../auth/authSelectors";
import * as routeHelpers from "../../appRoutingHelpers";
import PromotionCodeListFilters from "./PromotionCodeListFilters";
import { withTranslation } from "react-i18next";
import { StyledText } from "../../assets/typography/typography";
import { PrimaryButton } from "../../components/Buttons";
import { colors } from "../../components/colors";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { PromotionCodeListRow } from "./PromotionCodeListRow";
import { ButtonInternalLink } from "../../components/NewButtons";

interface Props {
  isLoading: boolean;
  promotionCodes: PromotionCodeListItem[];
  getPromotionCodes: (
    org: OrganizationKeyName,
    page: number,
    filters: { [i: string]: string }
  ) => void;
  organization: OrganizationKeyName;
  hasNextPage: boolean;
}

interface State {
  page: number;
  filters: { [i: string]: string };
}

const mapStateToProps = (state: RootState, ownProps: {}) => {
  const org = authSelectors.selectedOrganizationKeyName(state);
  return {
    organization: org,
    promotionCodes: promotionCodeSelectors.promotionCodeList(state, org),
    isLoading: promotionCodeSelectors.isLoadingPromotionCodeList(state, org),
    hasNextPage: promotionCodeSelectors.hasNextPage(state, org),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    getPromotionCodes: (
      org: OrganizationKeyName,
      page: number,
      filters: { [i: string]: string }
    ) =>
      dispatch(
        promotionCodeActions.listPromotionCodesAction(org, page, filters)
      ),
  };
};

class PromotionCodeList extends React.Component<Props, State> {
  state = {
    page: 1,
    filters: {},
  };

  onSearch = (values: { [i: string]: string }) => {
    this.setState({ filters: values, page: 1 }, () => {
      this.loadData();
    });
  };

  onReset = () => {
    this.setState({ filters: {}, page: 1 }, () => {
      this.loadData();
    });
  };

  loadData() {
    this.props.getPromotionCodes(
      this.props.organization,
      this.state.page,
      this.state.filters
    );
  }

  loadNextPage = (e: React.MouseEvent) => {
    e.preventDefault();

    this.setState({ page: this.state.page + 1 }, () => {
      this.loadData();
    });
  };

  componentDidMount() {
    this.loadData();
  }

  render() {
    const t = (this.props as any).t;

    return (
      <div className="container mt-7">
        <div className="row row-cards row-deck">
          <div className="col-12">
            <PromotionCodeListFilters
              onSubmit={this.onSearch}
              onReset={this.onReset}
              currentFilters={this.state.filters}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header border-bottom-0 mt-4 mb-4 justify-content-between">
                <StyledText
                  color={colors.textPrimary}
                  className="bodyHeaderOneSemi card-title"
                >
                  Promotion Codes List
                </StyledText>
                <ButtonInternalLink
                  to={routeHelpers.routes.promotionCodeAdd(
                    this.props.organization
                  )}
                >
                  Add new
                </ButtonInternalLink>
              </div>
              <div className="pl-5 pr-5">
                <Table>
                  <thead>
                    <TableRowHeader>
                      <TableCellHeaderText className="pl-3">
                        Type
                      </TableCellHeaderText>
                      <TableCellHeaderText>Code</TableCellHeaderText>
                      <TableCellHeaderText>User</TableCellHeaderText>
                      <TableCellHeaderText>Valid From</TableCellHeaderText>
                      <TableCellHeaderText>Valid To</TableCellHeaderText>
                    </TableRowHeader>
                  </thead>
                  <tbody>
                    {this.props.promotionCodes.map((pc) => (
                      <PromotionCodeListRow
                        href={routes.promotionCodeEdit(
                          this.props.organization,
                          pc.id
                        )}
                        promotionCode={pc}
                        translation={t}
                        key={pc.id}
                      />
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="card-footer text-center mr-5 ml-5 px-0">
                {this.props.isLoading ? (
                  <Spinner />
                ) : (
                  <>
                    {this.props.hasNextPage ? (
                      <PrimaryButton
                        backgroundColor={colors.white}
                        hoverColor={colors.ctaLight}
                        width="100%"
                        color={colors.textPrimary}
                        onClick={this.loadNextPage}
                      >
                        Load more
                      </PrimaryButton>
                    ) : (
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        No more promotion codes
                      </StyledText>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(PromotionCodeList));
